/**
 * VideoModal — lecteur Vimeo en surimpression, pour les pages publiques.
 *
 * Deux règles portent tout le reste :
 *
 *  1. L'iframe n'existe QUE pendant que la modale est ouverte. Fermée, le
 *     composant rend `null` : rien n'est demandé à vimeo.com tant que le
 *     visiteur n'a pas cliqué, et la fermeture démonte le lecteur, donc le
 *     son s'arrête net (pas de pause à distance à orchestrer, pas d'API
 *     player à charger).
 *
 *  2. `dnt=1` dans l'URL du lecteur. C'est le paramètre « do not track »
 *     de Vimeo : ni cookie, ni mesure d'audience. C'est lui qui rend
 *     l'intégration compatible avec notre bandeau de consentement sans
 *     acceptation préalable — ne jamais le retirer.
 *
 * Le son est ACTIF (pas de `muted=1`) : la voix off est le contenu même
 * de la vidéo, et le clic délibéré du visiteur vaut autorisation de
 * lecture sonore pour les navigateurs.
 *
 * Accessibilité : `role="dialog"` + `aria-modal`, focus déplacé sur la
 * croix à l'ouverture et rendu au déclencheur à la fermeture, tabulation
 * piégée dans le panneau, défilement du corps bloqué, fermeture par croix
 * / fond / Échap.
 *
 * Vidéo injoignable : voir `onUnavailable`. Une vidéo supprimée ou mal
 * partagée affiche sinon un rectangle noir « Désolé » servi par Vimeo,
 * dont nous ne pouvons rien faire — c'est ce qui tournait en production
 * le 14/08/2026, les trois vidéos ayant été remplacées chez Vimeo.
 */
import React, { useCallback, useEffect, useRef } from 'react';
import './siteV2.css';

/** Sélecteur des éléments qui peuvent recevoir le focus dans le panneau. */
const FOCUSABLE = 'button, [href], iframe, input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * URL du lecteur. `title`/`byline`/`portrait` à 0 retirent le bandeau
 * d'auteur de Vimeo : la vidéo est chez nous, pas sur leur site.
 *
 * `hash` est le jeton de confidentialité des vidéos NON RÉPERTORIÉES. Il
 * passe par le paramètre `h=` — le coller au numéro dans le chemin donne
 * un 404. Une vidéo répertoriée s'embarque sans lui.
 *
 * @param {{id: string, hash?: string}} video
 */
export function vimeoEmbedUrl({ id, hash } = {}) {
  const privacy = hash ? `h=${hash}&` : '';
  return `https://player.vimeo.com/video/${id}?${privacy}autoplay=1&dnt=1&title=0&byline=0&portrait=0`;
}

const IconClose = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/**
 * @param {boolean} open      monte le lecteur ; `false` ne rend rien
 * @param {{id: string, hash?: string}} video  vidéo Vimeo (hash = non répertoriée)
 * @param {() => void} onClose
 * @param {string}  label     nom de la boîte de dialogue (aria-label)
 * @param {string}  closeLabel  libellé accessible de la croix
 * @param {boolean} reducedMotion  supprime le fondu d'ouverture
 * @param {() => void} [onUnavailable]  le lecteur a refusé la vidéo
 */
export default function VideoModal({
  open,
  video,
  onClose,
  label,
  closeLabel,
  reducedMotion = false,
  testId = 'video-modal',
  onUnavailable,
}) {
  const panelRef = useRef(null);
  const closeRef = useRef(null);

  const close = useCallback(() => { onClose?.(); }, [onClose]);

  /* Vidéo injoignable — supprimée, ou non répertoriée déclarée sans son
     hash. Le lecteur l'annonce lui-même en postMessage, et il distingue
     les deux familles d'incidents : `method: 'ready'` signifie « je n'ai
     même pas pu ouvrir cette vidéo » (`PrivacyError`, `NotFoundError`),
     alors qu'une erreur SANS `method` est un incident de lecture sur une
     vidéo bien chargée (`PlaybackError` — codec, réseau), qu'il ne faut
     surtout pas confondre avec elle : le visiteur regarde peut-être la
     vidéo, on ne lui ferme pas la fenêtre au nez. Mesuré au lecteur réel
     le 14/08/2026 ; aucun SDK Vimeo n'est chargé pour l'écouter. */
  useEffect(() => {
    if (!open || !onUnavailable) return undefined;
    const onMessage = (e) => {
      if (!/^https:\/\/player\.vimeo\.com/.test(e.origin)) return;
      let payload = e.data;
      if (typeof payload === 'string') {
        try { payload = JSON.parse(payload); } catch (_) { return; }
      }
      if (payload?.event === 'error' && payload?.data?.method === 'ready') onUnavailable();
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [open, onUnavailable]);

  /* Échap + piège à focus. Un seul écouteur, sur le document : la modale
     couvre l'écran, aucun autre gestionnaire n'a à voir ces touches. */
  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); close(); return; }
      if (e.key !== 'Tab') return;
      const items = panelRef.current?.querySelectorAll(FOCUSABLE);
      if (!items || items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      // Le focus peut être sorti du panneau (clic sur le fond) : on le
      // ramène sur le premier élément plutôt que de laisser la tabulation
      // filer vers la page derrière.
      if (!panelRef.current.contains(active)) { e.preventDefault(); first.focus(); return; }
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };

    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, close]);

  /* Défilement du corps bloqué, focus donné à la croix, focus rendu au
     déclencheur à la fermeture. La valeur d'origine d'`overflow` est
     restaurée telle quelle : une autre surface peut l'avoir posée. */
  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, [open]);

  if (!open || !video?.id) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label}
      data-testid={testId}
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1300,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'clamp(14px, 4vw, 40px)',
        background: 'rgba(10,10,15,.82)',
        animation: reducedMotion ? 'none' : 'sv2VideoFade .16s ease-out',
      }}
    >
      <div
        ref={panelRef}
        style={{
          position: 'relative',
          width: '100%',
          // Bornée aussi par la hauteur : en 16:9, un panneau de 1100 px
          // demande 619 px de haut, ce qu'un portable posé à plat n'a pas
          // toujours. Sans ce `min()`, la vidéo déborderait du cadre.
          maxWidth: 'min(1100px, calc((100vh - 120px) * 16 / 9))',
        }}
      >
        <button
          type="button"
          ref={closeRef}
          onClick={close}
          aria-label={closeLabel}
          data-testid="video-modal-close"
          style={{
            position: 'absolute', top: -52, right: 0,
            width: 44, height: 44, // cible tactile de 44 px, seuil AA
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 0, borderRadius: 999,
            background: 'rgba(255,255,255,.12)',
            border: '1px solid rgba(255,255,255,.26)',
            color: '#fff', cursor: 'pointer', fontFamily: 'inherit',
            transition: reducedMotion ? 'none' : 'background .15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,.24)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,.12)'; }}
        >
          <IconClose />
        </button>

        <div
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16 / 9',
            background: '#000',
            borderRadius: 14,
            overflow: 'hidden',
            boxShadow: '0 40px 90px -30px rgba(0,0,0,.7)',
          }}
        >
          <iframe
            src={vimeoEmbedUrl(video)}
            title={label}
            // `encrypted-media` fait partie de la liste que Vimeo réclame :
            // sans lui le lecteur tente quand même l'accès et le navigateur
            // journalise « Permissions policy violation: encrypted-media »
            // à chaque lecture. La vidéo passe, mais la console ne doit pas
            // porter une erreur que nous causons. Mesuré le 14/08/2026.
            allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
            allowFullScreen
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
          />
        </div>
      </div>
    </div>
  );
}
